import React, { useState, useMemo, useEffect } from "react";
import styles from "@/pages/query/styles.module.scss";
import { default as InsightIcon } from "@/assets/icons/insight.svg?react";
import classNames from "classnames";
import { isEmpty } from "@/helpers/utils";
import { OpportunitySummary } from "./OpportunitySummary";
import { UserInfo } from "@/components/tags";
import dayjs from "dayjs";
import { IconButton, Stack, Tooltip } from "@altimateai/lego";
import { useUsers } from "@/helpers/hooks";
import { CountTag, LinenumberTag, MoneySavingsTag, TimeSavingsTag } from "../tags";
import EffortTag from "@/pages/explorer/components/effortTag/EffortTag";
import StatusTag from "@/pages/explorer/components/statusTag/StatusTag";
import { getEffortLabel, getOpportunityStatus } from "@/pages/explorer/utils";
import { OpportunitySectionProps } from "../types";
import { Calendar2Icon, JiraIcon, LoadingSpinner } from "@/assets/icons";
import { EmptyScreen } from "@/pages/warehouse/misc";
import { Paginate } from "@/components/paginate";
import { useNavigate } from "react-router-dom";
import { WarehouseScheduleOpportunity } from "@/pages/warehouse/components/scheduler/constants";
import { Markdown } from "@altimateai/lego";
import { useMutation } from "@tanstack/react-query";
import { createJiraTicket } from "@/helpers/apis";
import { toast, ToastAction, Button } from "@altimateai/lego";
import { useAppState } from "@/modules/app/useAppContext";
import { ActionsDropdown } from "@/components/closingLoop";
import ExpandableText from "@/components/expandableText/ExpandableText";
import { useGlobalPlaygroundActions } from "@/modules/playground";
import { StreamStatus, useStreaming } from "@/modules/streaming";
import { IconSparkles } from "@tabler/icons-react";
import { GetOpportunitiesDataResponse } from "@/pages/explorer/types";
import { isExplanationContext } from "../constants";

type Report = {
  indices?: [[number, number], [number, number]];
  startLineNumber: number;
  endLineNumber: number;
  text: string;
};

export const reportsToSpans = (reports: Report[]): Partial<Report>[] => {
  if (isEmpty(reports)) return [];
  return reports
    .map(({ indices, ...rest }) =>
      (
        indices || [
          [
            [0, 0],
            [0, 0],
          ],
        ]
      ).map(index => ({ index, ...rest }))
    )
    .flat()
    .sort((a, b) => a.startLineNumber - b.startLineNumber);
};

const PAGE_SIZE = 6;
export const InsightSectionLegacy: React.FC<OpportunitySectionProps> = ({
  opportunities: _opportunities = [],
  selectedOpportunityId,
  setOpportunityId,
  sideContentMode = false,
  totalRuns,
  startDate,
  endDate,
  showMultiStatementError = false,
  extraContent,
  showScheduledEventsActivity = false,
  queryId,
  showActionsDropdown = false,
  integrationIds,
}) => {
  const { jiraTicketEnabled, enableOpportunityTicketCreation } = useAppState();
  const navigate = useNavigate();
  const { openPlayground } = useGlobalPlaygroundActions();
  const { getStreamStatus } = useStreaming();
  const { getById } = useUsers();
  const [page, setPage] = useState(() => {
    if (sideContentMode) return 0;
    const index = _opportunities?.findIndex(o => o.opportunity_id === selectedOpportunityId);
    if (index === -1) return 0;
    return Math.floor(index / PAGE_SIZE);
  });

  const opportunities = useMemo(() => {
    const opportunityLinenumberMap: Record<string, number> = {};
    const opportunityCountMap: Record<string, number> = {};

    // Count occurrences of each opportunity_name
    _opportunities.forEach(o => {
      opportunityCountMap[o.opportunity_name] = (opportunityCountMap[o.opportunity_name] || 0) + 1;
    });

    return _opportunities
      .map(o => {
        if (o.linenumber) {
          return o;
        }
        if (opportunityCountMap[o.opportunity_name] === 1) {
          // Do not add linenumber if only one item exists
          return o;
        }
        opportunityLinenumberMap[o.opportunity_name] =
          opportunityLinenumberMap[o.opportunity_name] || 0;
        opportunityLinenumberMap[o.opportunity_name] += 1;
        return {
          ...o,
          linenumber: String(opportunityLinenumberMap[o.opportunity_name]),
        };
      })
      .sort((a, b) => a.opportunity_name.localeCompare(b.opportunity_name));
  }, [_opportunities]);

  useEffect(() => {
    const index = opportunities?.findIndex(o => o.opportunity_id === selectedOpportunityId);
    if (index === -1) return;
    setPage(Math.floor(index / PAGE_SIZE));
  }, [opportunities, selectedOpportunityId]);

  const { mutate: jiraTicketMutation, isPending: isCreatingTicket } = useMutation({
    mutationFn: (variables: { opportunityId: string; queryId?: string }) =>
      createJiraTicket(variables.opportunityId, variables.queryId ?? null),
    onSuccess: data => {
      toast({
        title: `Successfully created Jira ticket`,
        description: `Jira ID: ${data?.issue_key}`,
        action: (
          <ToastAction
            altText="Copy Url"
            onClick={async () => await navigator.clipboard.writeText(data?.url ?? "")}
          >
            Copy URL
          </ToastAction>
        ),
        variant: "default",
      });
    },
    onError: error => {
      toast({
        title: "Failed to create Jira ticket",
        description: error instanceof Error ? error.message : String(error),
        variant: "destructive",
      });
    },
  });

  if (showMultiStatementError) {
    return (
      <EmptyScreen
        title="No Opportunities"
        subTitle="Opportunities for multi-statement SQL queries are not currently supported. We're actively working on this support."
      />
    );
  }

  if (!opportunities || opportunities.length === 0) {
    return (
      <EmptyScreen title="No Opportunities" subTitle="No opportunities found for this resource." />
    );
  }

  const pageOpportunities = sideContentMode
    ? opportunities
    : opportunities.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const navigateToScheduleTab = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate("?tab=3");
  };

  const handleCreateJiraTicket = (opportunityId: string) => {
    jiraTicketMutation({
      opportunityId,
      queryId,
    });
  };

  const handleShowExplanation = (
    e: React.MouseEvent<HTMLButtonElement>,
    item: GetOpportunitiesDataResponse
  ) => {
    e.stopPropagation();
    if (getStreamStatus(item.opportunity_id) === StreamStatus.Finished) {
      return;
    }
    openPlayground({
      chatbotProps: {
        initialMessage: `Explain this opportunity: ${item.opportunity_name}`,
      },
      context: {
        opportunity_id: item.opportunity_id,
        opportunity_name: item.opportunity_name,
      },
    });
  };

  return (
    <div className="mb-2 mt-2" aria-label="Opportunities section">
      <div
        className={classNames(styles.insight_container, {
          "overflow-auto": sideContentMode,
        })}
        style={{
          gridTemplateColumns: sideContentMode ? "1fr" : "repeat(auto-fit, minmax(500px, 1fr))",
          maxWidth: sideContentMode ? "100%" : "auto",
          overflowX: sideContentMode ? "hidden" : "visible",
        }}
      >
        {pageOpportunities.map(item => {
          const _selected = selectedOpportunityId === item.opportunity_id;
          const owner = getById(Number(item?.owner));
          return (
            <div
              className={classNames(styles.insight_card, {
                [styles.selected]: _selected,
              })}
              key={item.opportunity_id}
              role="button"
              tabIndex={0}
              onClick={() => setOpportunityId(item.opportunity_id)}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  setOpportunityId(item.opportunity_id);
                }
              }}
            >
              <Stack
                direction={!sideContentMode ? "column" : undefined}
                justify={!sideContentMode ? "between" : undefined}
                className={!sideContentMode ? "al-h-full" : ""}
              >
                <div>
                  <Stack gap="xs" align="center" className="mb-2">
                    <div className="icon-sm">
                      <InsightIcon />
                    </div>
                    <Stack align="center" gap="sm" className="fs-5 lines-2">
                      <span>{item.opportunity_name}</span>
                      {item.linenumber && (
                        <Tooltip
                          title={
                            item?.metadata?.report?.type === "global"
                              ? "Whole query"
                              : item?.metadata?.report?.actual_query_span ||
                                "Unable to map to query"
                          }
                        >
                          <LinenumberTag linenumber={item.linenumber} />
                        </Tooltip>
                      )}
                    </Stack>
                    <div className="spacer" />
                    {(!sideContentMode || !_selected) && (
                      <>
                        <Tooltip title={"Expected effort to realize this opportunity"}>
                          <EffortTag effort={getEffortLabel(item?.effort)} />
                        </Tooltip>
                        {item?.money_savings && (
                          <Tooltip title="Projected money savings during the time period">
                            <MoneySavingsTag savings={item?.money_savings} />
                          </Tooltip>
                        )}
                        {item?.time_savings && (
                          <Tooltip title="Projected time savings during the time period">
                            <TimeSavingsTag savings={item?.time_savings} />
                          </Tooltip>
                        )}
                        {jiraTicketEnabled && (
                          <Button
                            title="Create Jira Ticket"
                            variant={"outline"}
                            size={"icon"}
                            disabled={isCreatingTicket}
                            onClick={event => {
                              event.stopPropagation();
                              handleCreateJiraTicket(item.opportunity_id);
                            }}
                          >
                            {isCreatingTicket ? <LoadingSpinner /> : <JiraIcon />}
                          </Button>
                        )}
                        {enableOpportunityTicketCreation && showActionsDropdown && (
                          <ActionsDropdown
                            opportunity={{
                              opportunity_id: item.opportunity_id,
                              title: item.opportunity_name,
                              description: item.description || "Query optimization opportunity",
                              impact: isExplanationContext(item.explanation_context)
                                ? item.explanation_context
                                : undefined,
                              savings: item.money_savings?.max
                                ? parseFloat(item.money_savings.max)
                                : item.money_savings?.min
                                  ? parseFloat(item.money_savings.min)
                                  : undefined,
                              category: item.money_savings ? "savings" : "performance",
                              resources:
                                item.resources?.map(r => ({
                                  resource_name: r.resource_name,
                                  resource_uri: r.resource_uri,
                                })) || [],
                            }}
                            integrationIds={integrationIds}
                          />
                        )}
                      </>
                    )}
                    {sideContentMode && Boolean(item.explanation_context) && (
                      <Button
                        variant={"default"}
                        size="icon"
                        className={"al-bg-brand !al-p-2 !al-h-auto !al-w-auto"}
                        title="Show explanation"
                        onClick={e => handleShowExplanation(e, item)}
                      >
                        <IconSparkles />
                      </Button>
                    )}
                  </Stack>
                  <div className="text-muted">
                    <ExpandableText lines={3} buttonClassName="al-text-primary al-text-[10px]">
                      <Markdown
                        content={item.description || ""}
                        components={{
                          a: ({ node: _node, children, ...props }) => (
                            <a
                              {...props}
                              className="al-text-brand hover:al-underline"
                              target="_blank"
                            >
                              {children}
                            </a>
                          ),
                        }}
                      />
                    </ExpandableText>
                  </div>
                </div>
                {(!sideContentMode || !_selected) && (
                  <Stack justify="between" align="center">
                    <Stack justify="between" gap="sm">
                      <Tooltip
                        title={
                          totalRuns ? (
                            <div>
                              This opportunity was detected {item?.count} times in {totalRuns} query
                              runs that happened.
                            </div>
                          ) : (
                            <div>This opportunity was detected {item?.count} times.</div>
                          )
                        }
                      >
                        <CountTag count={item?.count} />
                      </Tooltip>
                      <div className={styles.seen}>
                        <Tooltip
                          title={
                            <>
                              <div>First seen</div>
                              <div>{dayjs(item?.first_seen).format("DD MMM YYYY hh:mm:ss A")}</div>
                            </>
                          }
                        >
                          <div className="cursor-pointer">{dayjs(item?.first_seen).fromNow()}</div>
                        </Tooltip>{" "}
                        |{" "}
                        <Tooltip
                          title={
                            <>
                              <div>Last seen</div>
                              <div>{dayjs(item?.last_seen).format("DD MMM YYYY hh:mm:ss A")}</div>
                            </>
                          }
                        >
                          <div className="cursor-pointer">{dayjs(item?.last_seen).fromNow()}</div>
                        </Tooltip>
                      </div>
                    </Stack>
                    <Stack gap="sm" align="center">
                      {item?.opportunity_name == WarehouseScheduleOpportunity && (
                        <IconButton
                          title={"View schedule"}
                          className={styles.calendarIcon}
                          onClick={navigateToScheduleTab}
                        >
                          <Calendar2Icon />
                        </IconButton>
                      )}
                      {item?.owner && item.owner !== "-1" && owner && <UserInfo user={owner} />}
                      {(item?.status && (
                        <StatusTag status={getOpportunityStatus(item?.status)} />
                      )) ||
                        null}
                    </Stack>
                  </Stack>
                )}
              </Stack>
              {_selected && sideContentMode && (
                <div className="al-w-full overflow-hidden">
                  <OpportunitySummary
                    opportunity={item}
                    startDate={startDate}
                    endDate={endDate}
                    savingsInfo={item?.savings_attribution}
                    extraContent={extraContent}
                    showScheduledEventsActivity={showScheduledEventsActivity}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
      {!sideContentMode && opportunities?.length > PAGE_SIZE && (
        <Paginate
          itemCount={opportunities?.length}
          page={page}
          pageSize={PAGE_SIZE}
          numPages={Math.ceil(opportunities?.length / PAGE_SIZE)}
          onPageClick={setPage}
          onPageSizeChange={() => {}}
        />
      )}
    </div>
  );
};
